/** @jsxImportSource @emotion/react */

import React, { useState, useEffect } from "react";
import UIButton from "../components/Button";
import "../styles/auth.css";
import EmailInput from "../components/auth/EmailInput";
import useVerifyOtp from "../hooks/useVerifyOtp";
import useSendOtp from "../hooks/useSendOtp";
import { useLocation, useNavigate } from "react-router-dom";
import { storage } from "../utils/storage";
import { css } from "@emotion/react";

const Confirm = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { email: navEmail, otpId: navOtpId } =
    (location.state as { email?: string; otpId?: string }) || {};
  const initialEmail = navEmail ?? storage.getUserEmail() ?? "";
  const initialOtpId = navOtpId ?? "";

  const [localEmail, setLocalEmail] = useState(initialEmail);
  const [isValid, setIsValid] = useState<boolean | null>(null);
  const [confirmCode, setConfirmCode] = useState("");
  const [showAlert, setShowAlert] = useState(false);
  const [currentOtpId, setCurrentOtpId] = useState(initialOtpId);
  const [cooldownRemaining, setCooldownRemaining] = useState(0);
  const [alertMessage, setAlertMessage] = useState<string | null>(null);
  const [alertType, setAlertType] = useState<"success" | "error" | null>(null);

  const { verifyOtp, userData } = useVerifyOtp();
  const { sendOtp, otpId: resendOtpId, loading: sendingOtp } = useSendOtp();

  const handleChange = (e: { target: { value: string } }) => {
    const value = e.target.value.trim();
    setConfirmCode(value);
    setIsValid(value.length === 6);
    console.log("✅ OTP Code:", value);
  };

  const handleConfirmOtp = async () => {
    const code = confirmCode.trim();
    const emailToUse = localEmail || initialEmail;
    const otpToUse = currentOtpId || initialOtpId;

    console.log("🔍 Checking before sending OTP verification:", {
      code,
      emailToUse,
      otpToUse,
    });

    if (!emailToUse || !otpToUse || code.length !== 6) {
      console.error("❌ Missing required parameters:", {
        email: emailToUse,
        code,
        otpId: otpToUse,
      });
      alert("Please enter a valid 6-digit OTP code!");
      return;
    }

    console.log("==>> Confirming OTP with:", {
      email: emailToUse,
      code,
      otpId: otpToUse,
    });

    try {
      await verifyOtp({ email: emailToUse, code, otpId: otpToUse });
    } catch (err) {
      console.error("❌ OTP verification failed:", err);
    }
  };

  const handleResendCode = async () => {
    if (cooldownRemaining > 0 || sendingOtp) return;

    setShowAlert(false);
    setAlertMessage(null);
    setAlertType(null);

    const emailToUse = localEmail || initialEmail;
    if (!emailToUse) {
      return;
    }

    const { otpId: gotOtp } = await sendOtp(emailToUse);

    if (gotOtp) {
      setCurrentOtpId(gotOtp || "");
      setAlertMessage("OTP Code was sent");
      setAlertType("success");
      setShowAlert(true);
      setCooldownRemaining(60);
      return;
    }
  };

  useEffect(() => {
    if (resendOtpId && resendOtpId !== currentOtpId) {
      console.log("===>>> Received new OTP ID from resend:", resendOtpId);
      setCurrentOtpId(resendOtpId);
    }
  }, [resendOtpId, currentOtpId]);

  useEffect(() => {
    if (cooldownRemaining <= 0) return;
    const id = window.setInterval(() => {
      setCooldownRemaining((s) => (s > 0 ? s - 1 : 0));
    }, 1000);
    return () => window.clearInterval(id);
  }, [cooldownRemaining]);

  // tự động đăng nhập khi nhập đủ 6 số otp
  useEffect(() => {
    if (confirmCode.length === 6) {
      handleConfirmOtp();
    }
  }, [confirmCode]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!initialEmail) {
      navigate("/get-started");
    }
  }, [initialEmail, navigate]);

  useEffect(() => {
    if (userData) {
      navigate("/");
    }
  }, [userData, navigate]);

  return (
    <>
      <div className="flex justify-center w-full h-auto">
        <div
          className="auth-container relative flex flex-col items-center justify-center w-full"
          css={css`
            @media (max-width: 1023px) {
              padding: 0;
              margin-top: 40px;
            }
          `}>
          <div className="w-full max-w-md px-6 flex flex-col">
            <h1 className="text-left my-2">Confirm</h1>
            <p>
              We have sent a confirmation code to your email. Please enter that
              code to complete
            </p>
            <p className="text-left pl-4 text-white mt-4">Confirmation Code</p>

            <EmailInput
              email={confirmCode}
              setEmail={setConfirmCode}
              setIsValid={setIsValid}
              textError={"Invalid Code"}
              text={"6 digit code"}
            />
            <UIButton
              isValid={true}
              text="Resend Code"
              onClick={handleResendCode}
              backgroundColor="bg-grey"
              textColor="text-base text-btn-color"
            />
            {showAlert && alertMessage && (
              <div
                className={`mt-4 px-4 py-2 rounded-xl text-sm text-white flex items-center justify-between ${
                  alertType === "success"
                    ? `notification-success`
                    : "bg-red-600"
                }`}>
                {alertType === "success" && (
                  <img
                    src="/assets/images/icon-ok-confirm.svg"
                    alt="success"
                    className="w-6 h-6"
                  />
                )}

                <span>{alertMessage}</span>
                <button
                  type="button"
                  className="ml-4 font-bold"
                  onClick={() => setShowAlert(false)}>
                  <img
                    src="/assets/images/icon-close.svg"
                    alt="close"
                    className="w-6 h-6"
                  />
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
      {/* footer */}
      <div
        className="text-color-desc text-sm my-4 flex justify-between fixed bottom-0 left-0 right-0 p-4 lg:w-1/2 lg:px-8"
        css={css`
          @media (max-width: 767px) {
            display: flex;
            flex-direction: column;
            gap: 16px;
          }
        `}>
        <div>
          <span>© 2025 WitWorkApp</span>
        </div>

        <div>
          <span
            className="underline cursor-pointer"
            onClick={() =>
              window.open(
                "https://witworkapp.com/privacy-policy.html",
                "_blank"
              )
            }>
            Private Policy
          </span>{" "}
          <span
            className="underline cursor-pointer"
            onClick={() =>
              window.open("https://witworkapp.com/terms-of-use.html", "_blank")
            }>
            Terms of Service
          </span>
        </div>
      </div>
    </>
  );
};

export default Confirm;
